import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';
import { FileUploadArea } from './FileUploadArea';
import { PreProcessingSummary, ImportResult } from './PreProcessingSummary';
import { UploadHistory } from './UploadHistory';
import { ChapterMapConfig } from './ChapterMapConfig';

export interface PreProcessingReport {
  uploadId: string;
  fileName: string;
  fileReferenceId: string;
  uploadDate: string;
  guestCount: number;
  orderCount: number;
  uploadedByName: string;
  duplicateCount: number;
  duplicateReferenceIds: string[];
}

const formatGuideStyles: Record<string, React.CSSProperties> = {
  wrapper: {
    marginBottom: '1rem',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.4rem 0',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#1a6b3c',
  },
  content: {
    marginTop: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f0f7f4',
    border: '1px solid #c8e6c9',
    borderRadius: '6px',
    fontSize: '0.78rem',
    lineHeight: 1.7,
    color: '#374151',
  },
  sectionLabel: {
    fontWeight: 700,
    color: '#0f2d1f',
    marginTop: '0.5rem',
    marginBottom: '0.25rem',
    display: 'block',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '0.35rem',
    fontSize: '0.75rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.3rem 0.5rem',
    backgroundColor: '#e8f5e9',
    borderBottom: '1px solid #c8e6c9',
    fontWeight: 600,
    color: '#1a6b3c',
  },
  td: {
    padding: '0.25rem 0.5rem',
    borderBottom: '1px solid #e8f5e9',
    color: '#4b5563',
  },
  note: {
    marginTop: '0.5rem',
    padding: '0.35rem 0.5rem',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '4px',
    fontSize: '0.75rem',
    color: '#92400e',
  },
};

function ImportFormatGuide() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={formatGuideStyles.wrapper}>
      <button
        type="button"
        style={formatGuideStyles.toggle}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span>{expanded ? '▼' : '▶'}</span>
        <span>📋 Expected File Format</span>
      </button>

      {expanded && (
        <div style={formatGuideStyles.content}>
          <p style={{ margin: '0 0 0.5rem 0' }}>
            The import file must be an <strong>.xlsx</strong> Excel file with a header row followed by data rows.
            Each row represents one order. Guest columns repeat in groups for multi-ticket orders.
          </p>

          <span style={formatGuideStyles.sectionLabel}>Order-Level Columns (A–F):</span>
          <table style={formatGuideStyles.table}>
            <thead>
              <tr>
                <th style={formatGuideStyles.th}>Column</th>
                <th style={formatGuideStyles.th}>Header</th>
                <th style={formatGuideStyles.th}>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={formatGuideStyles.td}>A</td><td style={formatGuideStyles.td}>Reference #</td><td style={formatGuideStyles.td}>19723244</td></tr>
              <tr><td style={formatGuideStyles.td}>B</td><td style={formatGuideStyles.td}>Status</td><td style={formatGuideStyles.td}>Completed</td></tr>
              <tr><td style={formatGuideStyles.td}>C</td><td style={formatGuideStyles.td}>Amount</td><td style={formatGuideStyles.td}>300</td></tr>
              <tr><td style={formatGuideStyles.td}>D</td><td style={formatGuideStyles.td}>Payment</td><td style={formatGuideStyles.td}>Paid</td></tr>
              <tr><td style={formatGuideStyles.td}>E</td><td style={formatGuideStyles.td}>Email Address for receipt</td><td style={formatGuideStyles.td}>john@example.com</td></tr>
              <tr><td style={formatGuideStyles.td}>F</td><td style={formatGuideStyles.td}>How many tickets are you purchasing?</td><td style={formatGuideStyles.td}>2</td></tr>
            </tbody>
          </table>

          <span style={formatGuideStyles.sectionLabel}>Guest Group Columns (repeating per ticket):</span>
          <table style={formatGuideStyles.table}>
            <thead>
              <tr>
                <th style={formatGuideStyles.th}>Position</th>
                <th style={formatGuideStyles.th}>Header</th>
                <th style={formatGuideStyles.th}>Example</th>
                <th style={formatGuideStyles.th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={formatGuideStyles.td}>1st</td><td style={formatGuideStyles.td}>First Name</td><td style={formatGuideStyles.td}>John</td><td style={formatGuideStyles.td}>Required</td></tr>
              <tr><td style={formatGuideStyles.td}>2nd</td><td style={formatGuideStyles.td}>Last Name</td><td style={formatGuideStyles.td}>Smith</td><td style={formatGuideStyles.td}>Required</td></tr>
              <tr><td style={formatGuideStyles.td}>3rd…</td><td style={formatGuideStyles.td}>(extra columns)</td><td style={formatGuideStyles.td}>—</td><td style={formatGuideStyles.td}>Phone, etc. (ignored)</td></tr>
              <tr><td style={formatGuideStyles.td}>3rd-to-last</td><td style={formatGuideStyles.td}>Chapter</td><td style={formatGuideStyles.td}>MD</td><td style={formatGuideStyles.td}>Chapter affiliation</td></tr>
              <tr><td style={formatGuideStyles.td}>2nd-to-last</td><td style={formatGuideStyles.td}>Ticket Type</td><td style={formatGuideStyles.td}>General (Standard)</td><td style={formatGuideStyles.td}>Must match a tier: General, Patron</td></tr>
              <tr><td style={formatGuideStyles.td}>Last</td><td style={formatGuideStyles.td}>Dinner Option</td><td style={formatGuideStyles.td}>Chicken</td><td style={formatGuideStyles.td}>Optional</td></tr>
            </tbody>
          </table>

          <span style={formatGuideStyles.sectionLabel}>Date Column (after all guest groups):</span>
          <p style={{ margin: '0.25rem 0' }}>A "Date" column at the end of the row.</p>

          <div style={formatGuideStyles.note}>
            <strong>⚠️ Important:</strong> The system detects the guest group size automatically by measuring the distance
            between "First Name" headers. Make sure each guest group has the same number of columns.
            The <strong>Ticket Type</strong> must exactly match a configured tier name (e.g., "General" or "Patron").
            Unknown tier values will cause that order to be skipped.
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '1.25rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '1rem',
    marginTop: 0,
  },
  placeholder: {
    padding: '1rem',
    backgroundColor: '#f9fafb',
    border: '1px dashed #d1d5db',
    borderRadius: '6px',
    color: '#6b7280',
    fontSize: '0.85rem',
    textAlign: 'center' as const,
    marginTop: '1rem',
  },
  cancelledMessage: {
    marginTop: '1rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#6b7280',
    textAlign: 'center' as const,
  },
  errorMessage: {
    marginTop: '1rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fdecea',
    border: '1px solid #f5c6cb',
    borderRadius: '6px',
    fontSize: '0.85rem',
    color: '#d32f2f',
  },
};

export function ImportOrdersSection() {
  const { user } = useAuth();
  const [report, setReport] = useState<PreProcessingReport | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [cancelledMessage, setCancelledMessage] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Only render for Admin or Organizer roles
  if (!user || !['Admin', 'Organizer'].includes(user.role)) {
    return null;
  }

  const handleFileProcessed = (preProcessingReport: PreProcessingReport) => {
    setReport(preProcessingReport);
    setIsProcessing(false);
    setResult(null);
    setConfirmError(null);
    setCancelledMessage(null);
  };

  const handleProcessingStart = () => {
    setIsProcessing(true);
    setReport(null);
    setResult(null);
    setConfirmError(null);
    setCancelledMessage(null);
  };

  const confirmImport = async () => {
    if (!report) return;
    setIsConfirming(true);
    setConfirmError(null);

    try {
      const importResult = await apiClient<ImportResult>(
        `/order-import/confirm/${report.uploadId}`,
        { method: 'POST' },
      );
      setResult(importResult);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setConfirmError(apiErr?.message || 'Failed to confirm import. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  const cancelImport = async () => {
    if (!report) return;
    setIsConfirming(true);
    setConfirmError(null);

    try {
      await apiClient(`/order-import/cancel/${report.uploadId}`, { method: 'POST' });
      setCancelledMessage('Import cancelled. Parsed data has been discarded.');
      setReport(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setConfirmError(apiErr?.message || 'Failed to cancel import. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Import Orders</h3>

      <ImportFormatGuide />

      <FileUploadArea
        onProcessingStart={handleProcessingStart}
        onFileProcessed={handleFileProcessed}
        isProcessing={isProcessing}
      />

      {report && (
        <PreProcessingSummary
          report={report}
          onConfirm={confirmImport}
          onCancel={cancelImport}
          isConfirming={isConfirming}
          result={result}
        />
      )}

      {confirmError && (
        <div style={styles.errorMessage}>{confirmError}</div>
      )}

      {cancelledMessage && (
        <div style={styles.cancelledMessage}>{cancelledMessage}</div>
      )}

      <UploadHistory refreshTrigger={refreshTrigger} />

      {/* ChapterMapConfig — chapter normalization map CRUD */}
      <ChapterMapConfig />
    </div>
  );
}
